import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { FileText, RefreshCw, Copy, Download, Loader2 } from "lucide-react";

/**
 * AdminPRD — Live view of /app/memory/PRD.md so the operator can always read
 * the current product brief + roadmap without opening the code.
 * Rendered with react-markdown + remark-gfm (tables, checklists, GFM).
 */
export default function AdminPRD() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await adminApi.get("/admin/prd");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load PRD");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(data?.content || "");
      toast.success("Full PRD copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const downloadMd = () => {
    const blob = new Blob([data?.content || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "HealthCircles_PRD.md";
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="py-16 flex items-center justify-center text-[#8A9992]"><Loader2 className="animate-spin mr-2" size={16} /> Loading PRD…</div>;
  if (!data?.exists) return <div className="p-8 text-[#8A9992]">PRD file not found at <code>/app/memory/PRD.md</code>.</div>;

  const updated = data.updated_at ? new Date(data.updated_at).toLocaleString() : "—";

  return (
    <div data-testid="admin-prd-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#8A9992]">Product Brief</div>
          <h1 className="text-2xl font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }} data-testid="admin-prd-title">
            HealthCircles PRD
          </h1>
          <div className="text-xs text-[#8A9992] mt-1">
            {data.lines?.toLocaleString()} lines · {(data.size_bytes/1024).toFixed(1)} KB · Last updated {updated}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={refreshing} className="btn-outline-emerald text-xs" data-testid="admin-prd-refresh">
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={copyAll} className="btn-outline-emerald text-xs" data-testid="admin-prd-copy">
            <Copy size={13} /> Copy
          </button>
          <button onClick={downloadMd} className="btn-emerald text-xs" data-testid="admin-prd-download">
            <Download size={13} /> Download .md
          </button>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[#EFECE5] shadow-sm p-6 md:p-10">
        <article className="prd-md" data-testid="admin-prd-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {data.content}
          </ReactMarkdown>
        </article>
      </div>

      <style>{`
        .prd-md { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, sans-serif; color: #1F2A24; line-height: 1.65; font-size: 14.5px; }
        .prd-md h1 { font-family: "Instrument Sans"; font-size: 28px; font-weight: 600; color: #0A4C36; margin: 32px 0 12px; letter-spacing: -0.01em; }
        .prd-md h2 { font-family: "Instrument Sans"; font-size: 22px; font-weight: 600; color: #0A4C36; margin: 30px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #EFECE5; }
        .prd-md h3 { font-family: "Instrument Sans"; font-size: 17px; font-weight: 600; color: #1F2A24; margin: 22px 0 8px; }
        .prd-md h4 { font-size: 15px; font-weight: 600; color: #1F2A24; margin: 16px 0 6px; }
        .prd-md p { margin: 10px 0; }
        .prd-md ul, .prd-md ol { margin: 8px 0 12px; padding-left: 24px; }
        .prd-md li { margin: 4px 0; }
        .prd-md strong { color: #0A4C36; font-weight: 600; }
        .prd-md em { color: #47554D; }
        .prd-md code { background: #F7F4EC; padding: 2px 6px; border-radius: 4px; font-size: 12.5px; color: #4A2C13; font-family: JetBrains Mono, Consolas, monospace; }
        .prd-md pre { background: #F7F4EC; border: 1px solid #EFECE5; border-radius: 12px; padding: 14px 16px; overflow-x: auto; font-size: 12.5px; margin: 12px 0; }
        .prd-md pre code { background: transparent; padding: 0; }
        .prd-md blockquote { border-left: 3px solid #0A4C36; padding: 4px 14px; margin: 12px 0; background: #F5FAF7; color: #47554D; border-radius: 0 8px 8px 0; }
        .prd-md hr { border: none; border-top: 1px solid #EFECE5; margin: 32px 0; }
        .prd-md a { color: #0A4C36; text-decoration: underline; text-underline-offset: 2px; }
        .prd-md table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 13px; }
        .prd-md th { background: #F7F4EC; text-align: left; font-weight: 600; padding: 10px 12px; border-bottom: 1px solid #EFECE5; }
        .prd-md td { padding: 10px 12px; border-bottom: 1px solid #EFECE5; vertical-align: top; }
        .prd-md tr:hover td { background: #FDFBF7; }
      `}</style>
    </div>
  );
}
